import {toastr} from 'react-redux-toastr';
import moment from 'moment';
import {initialize} from 'redux-form';
import {
  getJsonAsync,
  putJsonAsync,
  postJsonAsync
} from 'shared/actions';
import {loadTournament} from '../actions';
import {
  hideSquadDialog,
  displayEventsInDropDown,
  displaySquadDialog,
  displayNextMonth,
  displayPreviousMonth,
  displaySpecificMonth,
  displaySquads,
  displaySquadDeleteModal,
  displaySquadDeleteConfirmModal,
  hideSquadDeleteDialog
} from './actionTypes';
import { 
  getRepeatingDays,
  getAllDaysOfWeek,
  validateRepeatUntil,
  validateSquad
} from './utils';

export const setCalendarToTournamentStart = () => (dispatch, getState) => {
  const tournamentStartDate = getState().tournament.base.tournamentInfo.startDate;
  const startDate = moment(new Date(tournamentStartDate));

  let month = Number(startDate.format('M'));
  let year = Number(startDate.format('YYYY'));

  dispatch(displaySpecificMonth(month, year));
};

export const handleEventClick = (target, eventData, day) => (dispatch, getState) => {
  const readOnly = getState().tournament.base.tournamentInfo.readOnly;
  
  if (readOnly) { return; }
  
  const tournamentId = getState().tournament.base.tournamentInfo.id,
    tmsEndpoint = getState().tournament.base.tournamentInfo.tmsEndPoint,
    // Whew creating a new Date in JS like this, make sure to use forward slashes
    // and not hyphens. E.g. 01/01/2016 and not 01-01-2016
    // See this StackOverflow for more info: https://goo.gl/ZADGJV
    selectedDate = moment(new Date(`${day.year}/${day.month + 1}/${day.day}`)), // day.month starts from zero
    monthName = selectedDate.format('MMMM'),
    status = getState().tournament.status.currentStatus,
    squadInfo = target.props.eventData.data;

  dispatch(getJsonAsync(`tournament/${tournamentId}/events/`))
  .then(data => {
    dispatch(displayEventsInDropDown(data.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    dispatch(displaySquadDialog(`Update Squad (${monthName} ${day.day}, ${day.year})`));
    dispatch(initialize(`tournamentSquadForm`, {
      ...squadInfo,
      startDateTime: moment.parseZone(squadInfo.startDateTime),
      time: moment.parseZone(squadInfo.startDateTime).format('HH:mm'),
      origStartLane: squadInfo.startLane,
      origEndLane: squadInfo.endLane,
      canDelete: true,
      status: status,
      tmsEndpoint: tmsEndpoint
    }));
    setTimeout(() => {
      // focus field after animatation finished (~800ms) by directly accessing the dom
      document.querySelectorAll('.modal-dialog [role=combobox]')[0].focus();
    }, 800);
  });
};

export const validate = (data) => {

  if (data.until){
    // scrub times from datepicker values
    data.until = moment(data.until).format('YYYY-MM-DD');
  }

  let errors = {};

  errors = validateSquad(data);

  errors.until = validateRepeatUntil(data);

  return errors;
};

export const handleSave = data => (dispatch, getState) => new Promise((resolve) => {
  const time = data.time.split(/\:/);
  const startDateTime = data.startDateTime.set({ hour: time[0], minute: time[1] }).format();
  const body = {
    ...data,
    startDateTime,
    repeatingDays: getRepeatingDays(startDateTime, data.repeatOn, data.until),
    repeatOn: getAllDaysOfWeek(startDateTime, data.repeatOn),
    until: data.until
  };

  const promise = data.id ? putJsonAsync('squad', body) : postJsonAsync('squad', body);

  dispatch(promise)
  .then(() => {
    const tournamentId = getState().tournament.base.tournamentInfo.id;

    toastr.success('Saved!', 'Squad was successfully saved.');
    dispatch(hideSquadDialog());
    dispatch(retrieveSquads(tournamentId));
    dispatch(loadTournament(tournamentId));
    resolve();
  })
  .catch(() => resolve());
});

export const handleDayClick = (target, day) => (dispatch, getState) => {
  const readOnly = getState().tournament.base.tournamentInfo.readOnly;
  
  if (readOnly) { return; }

  const tournamentInfo = getState().tournament.base.tournamentInfo;
  const tournamentId = tournamentInfo.id,
        timeZoneDisplayName = tournamentInfo.timeZoneDisplayName,
        selectedDate = moment([day.year, day.month, day.day]),
        monthName = selectedDate.format('MMMM');

  dispatch(getJsonAsync(`tournament/${tournamentId}/events/`))
  .then(data => {
    dispatch(displayEventsInDropDown(data.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    dispatch(displaySquadDialog(`Create a Squad (${monthName} ${day.day}, ${day.year})`));
    dispatch(initialize(`tournamentSquadForm`, {
      startDateTime: selectedDate,
      tournamentEndDate: tournamentInfo.endDate,
      canRepeat: true,
      canDelete: false,
      timeZoneDisplayName,
      minDateUntil: selectedDate.format("MM/DD/YYYY")
    }));
    setTimeout(() => {
      // focus field after animatation finished (~800ms) by directly accessing the dom
      document.querySelectorAll('.modal-dialog [role=combobox]')[0].focus();
    }, 800);
  });
};

export const moveToNextMonth = () => (dispatch, getState) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id;

  dispatch(displayNextMonth());
  dispatch(retrieveSquads(tournamentId));
};

export const moveToPreviousMonth = () => (dispatch, getState) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id;

  dispatch(displayPreviousMonth());
  dispatch(retrieveSquads(tournamentId));
};

export const retrieveSquads = tournamentId => (dispatch, getState) => {
  const {
    year,
    month
  } = getState().tournament.squads;

  dispatch(getJsonAsync(`tournament/${tournamentId}/squads/${year}/${month}`))
  .then(data => dispatch(displaySquads(data.map((squad) => ({
    start: moment.parseZone(squad.startDateTime).format('YYYY-MM-DD'),
    end: moment.parseZone(squad.startDateTime).format('YYYY-MM-DD'),
    title: squad.description,
    description: squad.description,
    data: squad
  })))));
};

export const loadSquadCalendar = tournamentId => dispatch => {
  dispatch(setCalendarToTournamentStart());
  dispatch(retrieveSquads(tournamentId));
};

export const confirmSquadDelete = () => (dispatch) => {
  dispatch(displaySquadDeleteModal());
};

export const handleCloseDeleteModal = () => (dispatch) => {
  dispatch(hideSquadDeleteDialog());
};

export const handleConfirmSquadDelete = (option, confirmation) => (dispatch, getState) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id,
      squad = getState().form.tournamentSquadForm.values,
      eventIds = [squad.eventId];

  dispatch(putJsonAsync(`squad/${squad.id}/delete`, {TournamentId: tournamentId, EventIds: eventIds, 
    Choice: option, Confirmation: confirmation, ...squad}, false))
      .then((response) => {
        if (response.message == null) {
          toastr.success('Deleted!', 'Squad was successfully deleted.');
          dispatch(hideSquadDialog());
          dispatch(retrieveSquads(tournamentId));
        } else dispatch(displaySquadDeleteConfirmModal(null, response.message));
      });
};

export const handleSquadDelete = () => (dispatch, getState) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id,
      squad = getState().form.tournamentSquadForm.values,
      eventIds = [squad.eventId];
    
  dispatch(putJsonAsync(`squad/${squad.id}/delete`, {TournamentId: tournamentId, EventIds: eventIds, ...squad}, false))
      .then((response) => {
          if (response.choice) {
              dispatch(displaySquadDeleteConfirmModal(response.message,null,response.choice, response.confirmation));
              return;
          }
          if (response.choice == null && response.message == null) {
          toastr.success('Deleted!', 'Squad was successfully deleted.');
          dispatch(hideSquadDialog());
          dispatch(retrieveSquads(tournamentId));
        } else dispatch(displaySquadDeleteModal(response.message));
      });
};


// WEBPACK FOOTER //
// ./src/features/tournament/squads/actions.js